import {Dispatch, FormEvent, SetStateAction, useEffect, useState} from "react";
import {useForm} from "@tanstack/react-form";

import OTPInput from "@/components/OTPInput";
import RippleButton from "@/components/RippleButton";
import ErrorBox from "@/components/ErrorBox";
import ArrowLeft from "@/icons/arrow-left.svg?react";
import Countdown from "../Countdown";
import {logEvent} from "@/utils/analytics";
import useSignByOTPCode from "@/features/auth/hooks/useSignByOTPCode";
import useRequestOTPCode from "../../hooks/useRequestOTPCode";

type OTPInputFormProps = {
  otpEmail: string;
  fallbackEmail: string;
  onBack: () => void;
  isTryAgainButtonDisabled: boolean;
  setIsTryAgainButtonDisabled: Dispatch<SetStateAction<boolean>>;
  onSendLink: (email: string) => Promise<void>;
  isSendLinkPending: boolean;
};

// Length of the OTP code.
const OTPCodeLength = 6;

/** OTP input form
 * Props:
 * - otpEmail: The email address to which the OTP code was sent.
 * - fallbackEmail: The email address to use for fallback (e.g., sending a link).
 * - onBack: Callback function to handle the back button action.
 * - requestOTPCode: Mutation result for requesting a new OTP code.
 * - isTryAgainButtonDisabled: Boolean indicating if the "Try Again" button is disabled.
 * - setIsTryAgainButtonDisabled: Function to set the disabled state of the "Try Again" button.
 * - onSendLink: Function to send a sign-in link to the fallback email.
 * - isSendLinkPending: Boolean indicating if sending the link is in progress.
 *
 * Features:
 * - Renders an OTP input component for entering the OTP code.
 * - Handles OTP submission and validation.
 * - Provides options to resend the OTP code or send a sign-in link.
 * - Displays error messages related to OTP input.
 */
const OTPInputForm = ({
  otpEmail,
  fallbackEmail,
  onBack,
  isTryAgainButtonDisabled,
  setIsTryAgainButtonDisabled,
  onSendLink,
  isSendLinkPending,
}: OTPInputFormProps) => {
  const [otpErrorText, setOtpErrorText] = useState("");

  const signByOTPCode = useSignByOTPCode();
  const requestOTPCode = useRequestOTPCode();

  const submitOtpCode = (code: string) => {
    if (code.length !== OTPCodeLength) {
      setOtpErrorText("OTP kód musí mít 6 číslic.");
      return;
    }

    setOtpErrorText("");
    signByOTPCode.mutate(
      {email: otpEmail, code},
      {
        onSuccess: () => {
          logEvent("login_method", {method: "otp"});
        },
        onSettled: () => {
          // Clear the form on both success and error.
          form.reset();
        },
      },
    );
  };

  const form = useForm({
    defaultValues: {
      code: "",
    },
    onSubmit: async ({value}) => {
      submitOtpCode(value.code);
    },
  });

  useEffect(() => {
    const error = signByOTPCode.error;

    if (!error) return;

    setOtpErrorText(error.message);
  }, [signByOTPCode.error]);

  useEffect(() => {
    const error = requestOTPCode.error;

    if (!error) return;

    setOtpErrorText(error.message);
  }, [requestOTPCode.error]);

  const handleOtpSubmit = (event?: FormEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    void form.handleSubmit();
  };

  const handleResendOtp = async () => {
    setOtpErrorText("");

    if (!otpEmail) return;

    const isSent = await requestOTPCode.mutateAsync(otpEmail).catch(() => {
      // Prevent unhandled promise rejection. Errors are handled by the hook's state.
    });

    if (isSent) {
      setIsTryAgainButtonDisabled(true);
      form.reset();
    }
  };

  const handleEmailLinkFallback = async () => {
    await onSendLink(fallbackEmail);
    logEvent("email_link_requested", {source: "otp_fallback"});
  };

  const handleBack = () => {
    form.reset();
    setOtpErrorText("");
    onBack();
  };

  return (
    <div className="gap-g-2 flex w-full flex-col items-center">
      <button
        type="button"
        className="text-on-surface hover:bg-surface-container absolute top-4 left-4 rounded-full p-2 transition-colors"
        onClick={handleBack}
        aria-label="Zpět"
      >
        <ArrowLeft className="size-i-2" />
      </button>

      <h1 className="mb-g-1 mt-g-1 text-3xl font-bold">Zadejte kód</h1>
      <div className="px-0.5 text-center">
        Poslali jsme ho na
        <br />
        {otpEmail}
      </div>
      <form
        className="gap-g-3 flex w-full flex-col items-center"
        spellCheck="false"
        onSubmit={handleOtpSubmit}
      >
        <form.Field
          name="code"
          validators={{
            onSubmit: ({value}) =>
              value.length === OTPCodeLength
                ? undefined
                : "OTP kód musí mít 6 číslic.",
          }}
        >
          {(field) => (
            <OTPInput
              length={OTPCodeLength}
              value={(field.state.value as string | undefined) ?? ""}
              onChange={(value) => {
                field.handleChange(value);
                setOtpErrorText("");
              }}
              onComplete={(value) => {
                field.handleChange(value);
                setOtpErrorText("");
                void field.form.handleSubmit();
              }}
            />
          )}
        </form.Field>

        <form.Subscribe
          selector={(state) =>
            (
              state.fieldMeta as
                | Record<string, {errors?: Array<{message: string}>}>
                | undefined
            )?.code?.errors ?? []
          }
        >
          {(fieldErrors) => (
            <ErrorBox
              error={
                otpErrorText ||
                fieldErrors.map((error) => error.message).join(" ")
              }
              displayTime={10000}
            />
          )}
        </form.Subscribe>

        <form.Subscribe
          selector={(state) =>
            (state.values as {code?: string} | undefined)?.code ?? ""
          }
        >
          {(codeValue) => (
            <RippleButton
              className="w-[240px] text-center"
              type="submit"
              variant="primary"
              centerText={true}
              disabled={codeValue.length !== OTPCodeLength}
              isPending={signByOTPCode.isPending}
            >
              <span>Pokračovat</span>
            </RippleButton>
          )}
        </form.Subscribe>
      </form>

      <div className="gap-g-2 -mt-2 flex w-full flex-col items-center">
        <RippleButton
          className="text-on-surface/70 w-[240px] text-sm"
          variant="text"
          centerText={true}
          disabled={isTryAgainButtonDisabled}
          isPending={requestOTPCode.isPending}
          onClick={handleResendOtp}
        >
          {isTryAgainButtonDisabled ? (
            <>
              Odeslat znovu za{" "}
              <Countdown
                seconds={30}
                onEnd={() => setIsTryAgainButtonDisabled(false)}
              />
              &nbsp;s
            </>
          ) : (
            "Odeslat nový kód"
          )}
        </RippleButton>
      </div>

      <div className="text-on-surface/70 -mb-2 text-sm">
        Nejde to?{" "}
        <RippleButton
          variant="text"
          className="min-h-0 p-0 text-sm"
          onClick={handleEmailLinkFallback}
          isPending={isSendLinkPending}
        >
          Pošleme vám odkaz
        </RippleButton>
        .
      </div>
    </div>
  );
};

export default OTPInputForm;
